import { useSelector } from "react-redux";
import { AISelectedArea, AISelectedCredit } from "redux/slices/artifactAISkillSelections/artifactAISkillSelections";
import { ArtifactDetailsArea, ArtifactDetailsCredit } from "redux/slices/artifactDetails/artifactDetailsSlice";
import { AssociatedSkillsArea, AssociatedSkillsCredit } from "redux/slices/associatedSkills/associatedSkillsSlice";

export type AreaToSort = ArtifactDetailsArea | AssociatedSkillsArea;
export type CreditToSort = ArtifactDetailsCredit | AssociatedSkillsCredit;

const useSkillUtilities = () => {
  const artifactAISkillSelectionsRedux = useSelector((state: any) => state.artifactAISkillSelections?.data);

  const getAreaSubskillScoresSum = (aiSelectedArea: AISelectedArea | null | undefined) => {
    if (!aiSelectedArea || !aiSelectedArea.subskills) return 0;
    return aiSelectedArea.subskills.reduce((sum, subskill) => sum + (subskill.subskill_score || 0), 0);
  };

  const sortAreasAISelectionFirst = <T extends AreaToSort>(areas: T[]): T[] => {
    if (!areas) { 
      console.warn("No skills provided to sortAreasAISelectionFirst");
      return [];
    };
    if (!artifactAISkillSelectionsRedux) return areas;

    const aiSelectedAreas: T[] = [];
    const userSelectedAreas: T[] = [];

    areas.forEach((skill: T) => {
      const aiSkillSelection = artifactAISkillSelectionsRedux.find((aiAreaSelection: AISelectedArea) => aiAreaSelection.skill_name === skill.title) as AISelectedArea;
      const isSkillAISelected = !!aiSkillSelection && getAreaSubskillScoresSum(aiSkillSelection) > 0; // if ai selection has no subskill scores, we consider it not selected by AI.

      if (isSkillAISelected) {
        aiSelectedAreas.push(skill);
      } else {
        userSelectedAreas.push(skill);
      }
    });

    const sortedSkills = [...aiSelectedAreas, ...userSelectedAreas];

    return sortedSkills;
  };

  const sortCreditsAISelectionsFirst = <T extends CreditToSort>(areaName: string, credits: T[]): T[] => {
    const areaSelection = artifactAISkillSelectionsRedux?.find((aiAreaSelection: AISelectedArea) => aiAreaSelection.skill_name === areaName) as AISelectedArea;

    if (!areaSelection) {
      console.warn(`No AI selection found for area "${areaName}" in sortCreditsAISelectionsFirst`);
      return credits;
    };

    if (!credits) {
      console.warn("No credits provided to sortCreditsAISelectionsFirst");
      return [];
    };

    if (!artifactAISkillSelectionsRedux || !areaSelection.subskills) return credits;

    const aiSelectedCredits: T[] = [];
    const userSelectedCredits: T[] = [];

    credits.forEach((credit: T) => {
      const creditSelection = areaSelection.subskills ? areaSelection.subskills.find((subskill: AISelectedCredit) => subskill.subskill_name === credit.title) : null;
      const isCreditSelected = !!creditSelection && creditSelection.subskill_score > 0; // if credit subskill score is 0 we consider it not selected by AI.

      if (isCreditSelected) {
        aiSelectedCredits.push(credit);
      } else {
        userSelectedCredits.push(credit);
      }
    })

    const sortedCredits = [...aiSelectedCredits, ...userSelectedCredits];

    return sortedCredits;
  }

  const getSkillSelectionsHistoryKey = (artifactId: number | undefined) => `artifact_${artifactId}_skill_selections`;
  const getAreaSelectionsHistoryKey = (artifactId: number | undefined) => `artifact_${artifactId}_selected_areas`;

  return {
    getAreaSubskillScoresSum,
    sortAreasAISelectionFirst,
    sortCreditsAISelectionsFirst,
    getSkillSelectionsHistoryKey,
    getAreaSelectionsHistoryKey,
  }
};

export default useSkillUtilities;